
import {
    Check as CheckIcon,
    ContentCopy as CopyIcon
} from '@mui/icons-material';
import {
    Box,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import { useState } from 'react';

const CodeBlock = ({ title, code, language = 'json', showTitle = true }) => {
    const [copied, setCopied] = useState(false);

    // Light theme colors
    const colors = {
        background: '#f6f8fa',
        border: '#d0d7de',
        text: '#24292f',
        scrollbarTrack: '#f0f0f0',
        scrollbarThumb: '#c0c0c0',
        scrollbarThumbHover: '#a0a0a0',
        jsonKey: '#0969da',
        jsonString: '#0a3069',
        jsonBoolean: '#cf222e',
        jsonNumber: '#0550ae',
        jsonBracket: '#953800',
        jsonPunctuation: '#57606a',
        javaKeyword: '#cf222e',
        javaString: '#0a3069',
        javaComment: '#6e7781',
        javaMethod: '#8250df',
        javaOperator: '#953800',
        javaBracket: '#57606a',
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Enhanced syntax highlighting function using React components
    const highlightCode = (code, language) => {
        if (language === 'java') {
            return highlightJava(code);
        } else if (language === 'json') {
            return highlightJSON(code);
        }
        return code;
    };

    const highlightJSON = (code) => {
        const lines = code.split('\n');
        return lines.map((line, lineIndex) => {
            // Match JSON patterns with light colors
            const patterns = [
                { regex: /"([^"\\]|\\.)*"(\s*:)/g, color: colors.jsonKey, fontWeight: '600' }, // Keys
                { regex: /"([^"\\]|\\.)*"/g, color: colors.jsonString }, // String values
                { regex: /\b(true|false|null)\b/g, color: colors.jsonBoolean }, // Booleans/null
                { regex: /-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?/g, color: colors.jsonNumber }, // Numbers
                { regex: /([{}[\]])/g, color: colors.jsonBracket, fontWeight: '600' }, // Brackets
                { regex: /([,:])/g, color: colors.jsonPunctuation } // Commas/colons
            ];

            const matches = [];

            patterns.forEach(pattern => {
                let match;
                while ((match = pattern.regex.exec(line)) !== null) {
                    matches.push({
                        start: match.index,
                        end: match.index + match[0].length,
                        text: match[0],
                        color: pattern.color,
                        fontWeight: pattern.fontWeight
                    });
                }
                pattern.regex.lastIndex = 0; // Reset regex
            });

            // Sort matches by start position
            matches.sort((a, b) => a.start - b.start);

            // Remove overlapping matches
            const filteredMatches = [];
            let lastEnd = 0;
            matches.forEach(match => {
                if (match.start >= lastEnd) {
                    filteredMatches.push(match);
                    lastEnd = match.end;
                }
            });

            // Build the line with highlighted parts
            const lineParts = [];
            let currentPos = 0;

            filteredMatches.forEach(match => {
                // Add text before match
                if (match.start > currentPos) {
                    lineParts.push(line.slice(currentPos, match.start));
                }

                // Add highlighted match
                lineParts.push(
                    <span
                        key={`${lineIndex}-${match.start}`}
                        style={{
                            color: match.color,
                            fontWeight: match.fontWeight || 'normal'
                        }}
                    >
                        {match.text}
                    </span>
                );

                currentPos = match.end;
            });

            // Add remaining text
            if (currentPos < line.length) {
                lineParts.push(line.slice(currentPos));
            }

            return (
                <div key={lineIndex}>
                    {lineParts.length > 0 ? lineParts : line}
                </div>
            );
        });
    };

    const highlightJava = (code) => {
        const lines = code.split('\n');
        return lines.map((line, lineIndex) => {
            // Java patterns with light colors
            const patterns = [
                { regex: /\b(public|private|protected|static|final|class|interface|extends|implements|import|package|if|else|for|while|do|switch|case|default|break|continue|return|try|catch|finally|throw|throws|new|this|super|void|int|long|double|float|boolean|char|byte|short|String|Map|List|HashMap|ArrayList|Arrays|HttpHeaders|HttpEntity|ResponseEntity|RestTemplate|UriComponentsBuilder|HttpMethod)\b/g, color: colors.javaKeyword, fontWeight: '600' }, // Keywords
                { regex: /"([^"\\]|\\.)*"/g, color: colors.javaString }, // Strings
                { regex: /(\/\/.*$)/gm, color: colors.javaComment, fontStyle: 'italic' }, // Comments
                { regex: /(\/\*[\s\S]*?\*\/)/g, color: colors.javaComment, fontStyle: 'italic' }, // Block comments
                { regex: /(\w+)\s*\(/g, color: colors.javaMethod }, // Method calls
                { regex: /([=+\-*/%<>!&|^~])/g, color: colors.javaOperator }, // Operators
                { regex: /([{}[\]()])/g, color: colors.javaBracket, fontWeight: '600' }, // Brackets
                { regex: /;/g, color: colors.javaBracket } // Semicolons
            ];

            const matches = [];

            patterns.forEach(pattern => {
                let match;
                while ((match = pattern.regex.exec(line)) !== null) {
                    matches.push({
                        start: match.index,
                        end: match.index + match[0].length,
                        text: match[0],
                        color: pattern.color,
                        fontWeight: pattern.fontWeight,
                        fontStyle: pattern.fontStyle
                    });
                }
                pattern.regex.lastIndex = 0; // Reset regex
            });

            // Sort matches by start position
            matches.sort((a, b) => a.start - b.start);

            // Remove overlapping matches
            const filteredMatches = [];
            let lastEnd = 0;
            matches.forEach(match => {
                if (match.start >= lastEnd) {
                    filteredMatches.push(match);
                    lastEnd = match.end;
                }
            });

            // Build the line with highlighted parts
            const lineParts = [];
            let currentPos = 0;

            filteredMatches.forEach(match => {
                // Add text before match
                if (match.start > currentPos) {
                    lineParts.push(line.slice(currentPos, match.start));
                }

                // Add highlighted match
                lineParts.push(
                    <span
                        key={`${lineIndex}-${match.start}`}
                        style={{
                            color: match.color,
                            fontWeight: match.fontWeight || 'normal',
                            fontStyle: match.fontStyle || 'normal'
                        }}
                    >
                        {match.text}
                    </span>
                );

                currentPos = match.end;
            });

            // Add remaining text
            if (currentPos < line.length) {
                lineParts.push(line.slice(currentPos));
            }

            return (
                <div key={lineIndex}>
                    {lineParts.length > 0 ? lineParts : line}
                </div>
            );
        });
    };

    return (
        <Box sx={{ mb: 2 }}>
            {showTitle && title && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1.5 }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            color: '#1a202c'
                        }}
                    >
                        {title}
                    </Typography>
                    <Tooltip title={copied ? "Copied!" : "Copy code"}>
                        <IconButton
                            size="small"
                            onClick={copyToClipboard}
                            sx={{
                                color: copied ? '#10b981' : '#6b7280',
                                '&:hover': { backgroundColor: '#f3f4f6' }
                            }}
                        >
                            {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}

            <Paper
                elevation={0}
                sx={{
                    backgroundColor: colors.background,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }}
            >
                <Box
                    sx={{
                        p: 2.5,
                        fontFamily: '"Fira Code", "JetBrains Mono", "Cascadia Code", "Courier New", monospace',
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        color: colors.text,
                        maxHeight: '500px',
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: colors.scrollbarTrack,
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: colors.scrollbarThumb,
                            borderRadius: '4px',
                            '&:hover': {
                                background: colors.scrollbarThumbHover,
                            }
                        },
                    }}
                >
                    {highlightCode(code, language)}
                </Box>
            </Paper>
        </Box>
    );
};

export default CodeBlock;